import React from "react";

const TermsAndConditionsModal = ({ isOpen, onClose, onAccept }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">Terms and Conditions</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-6 overflow-y-auto flex-1">
          <div className="prose prose-sm max-w-none space-y-6">
            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">1. Introduction</h3>
              <p className="text-gray-700 leading-relaxed">
                Welcome to Neuracraft. By creating a company account and using our platform, you agree to be bound by these Terms and Conditions. 
                Please read them carefully before proceeding with registration.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">2. Service Description</h3>
              <p className="text-gray-700 leading-relaxed">
                Neuracraft provides a comprehensive dairy management platform that digitizes and streamlines milk supply chain operations. 
                Our services include milk collection tracking, payment management, company-to-company trading, and AI-powered features.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">3. Subscription and Payment</h3>
              <div className="text-gray-700 leading-relaxed space-y-2">
                <p>
                  <strong>Free Trial:</strong> New company accounts receive a 30-day free trial period. During this period, you have full access to all platform features.
                </p>
                <p>
                  <strong>Monthly Billing:</strong> After the trial period, your account will be billed monthly. You will be notified before the trial ends.
                </p>
                <p>
                  <strong>Subscription Status:</strong> Your company must maintain an active subscription to continue using the platform and accessing your data.
                </p>
              </div>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">4. Data Usage and Privacy</h3>
              <div className="text-gray-700 leading-relaxed space-y-2">
                <p>
                  <strong>Data Collection:</strong> We collect and store data you provide through the platform, including company information, 
                  farmer records, milk collection data, payment information, and other business-related data.
                </p>
                <p>
                  <strong>AI Training:</strong> By using Neuracraft, you acknowledge and agree that the data you provide may be used to train 
                  and improve our AI models. This includes but is not limited to:
                </p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>Milk journal images and annotations</li>
                  <li>Text recognition and OCR improvements</li>
                  <li>Predictive analytics for dairy operations</li>
                  <li>Natural language processing for the AI assistant</li>
                </ul>
                <p>
                  <strong>Data Security:</strong> We implement industry-standard security measures to protect your sensitive information. 
                  Personal identifiable information (PII) is anonymized when used for AI training purposes.
                </p>
                <p>
                  <strong>Privacy Protection:</strong> We have built-in privacy features that blur sensitive information (such as farmer names 
                  and company details) when images are shared in the annotation marketplace.
                </p>
              </div>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">5. Company Account Status</h3>
              <div className="text-gray-700 leading-relaxed space-y-2">
                <p>
                  Your company account may have one of the following statuses:
                </p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li><strong>Active:</strong> Full access to all platform features</li>
                  <li><strong>Paused:</strong> Temporary suspension of services (data remains accessible after reactivation)</li>
                  <li><strong>Suspended:</strong> Account suspended due to policy violations or non-payment</li>
                </ul>
                <p>
                  Neuracraft reserves the right to pause or suspend accounts in cases of:
                </p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>Violation of these Terms and Conditions</li>
                  <li>Fraudulent activity or misuse of the platform</li>
                  <li>Non-payment of subscription fees</li>
                  <li>Gross misconduct or behavior that harms other users</li>
                  <li>Data misuse or unauthorized access attempts</li>
                </ul>
              </div>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">6. User Responsibilities</h3>
              <div className="text-gray-700 leading-relaxed space-y-2">
                <p>As a company using Neuracraft, you agree to:</p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>Provide accurate and truthful information</li>
                  <li>Maintain the security of your account credentials</li>
                  <li>Use the platform in compliance with all applicable laws</li>
                  <li>Not attempt to circumvent security measures or access controls</li>
                  <li>Respect the rights and privacy of farmers, employees, and other platform users</li>
                  <li>Report any suspected security vulnerabilities or data breaches immediately</li>
                </ul>
              </div>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">7. Content Moderation</h3>
              <p className="text-gray-700 leading-relaxed">
                We use AI-powered content moderation to ensure appropriate content on the platform. Images and data that violate 
                our content policies (including adult content, violence, or fraudulent materials) will be rejected or removed.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">8. Intellectual Property</h3>
              <p className="text-gray-700 leading-relaxed">
                You retain ownership of the data you upload to Neuracraft. By using our services, you grant Neuracraft a license 
                to use, process, and analyze your data to provide services and improve our AI models. The Neuracraft platform, 
                including all software, designs, and trademarks, remains the property of Neuracraft.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">9. Limitation of Liability</h3>
              <p className="text-gray-700 leading-relaxed">
                Neuracraft is provided "as is" without warranties of any kind. We are not liable for any indirect, incidental, 
                or consequential damages arising from your use of the platform. Our total liability shall not exceed the amount 
                paid by you in the past 12 months.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">10. Termination</h3>
              <p className="text-gray-700 leading-relaxed">
                You may terminate your account at any time. We may terminate or suspend your account for violations of these terms. 
                Upon termination, your access to the platform will cease, but certain provisions of these terms will continue to apply.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">11. Changes to Terms</h3>
              <p className="text-gray-700 leading-relaxed">
                We reserve the right to modify these Terms and Conditions at any time. We will notify you of significant changes 
                via email or platform notification. Continued use of the platform after changes constitutes acceptance of the new terms.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">12. Contact Information</h3>
              <p className="text-gray-700 leading-relaxed">
                If you have questions about these Terms and Conditions, please contact us at: support@neuracraft.com
              </p>
            </section>

            <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-900 font-semibold">
                By clicking "Accept and Continue" below, you acknowledge that you have read, understood, and agree to be bound 
                by these Terms and Conditions.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-6 py-2.5 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onAccept}
            className="px-6 py-2.5 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors"
          >
            Accept and Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditionsModal;
